import { useRef, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { downloadRaidTemplate, useImportConcerns, type RaidImportResult } from "@/hooks/useConcerns";

/**
 * RAID import from a spreadsheet (Brett, 2026-10-02): the team keeps the
 * log in Excel; typing it in twice is what this removes. Pick a file,
 * read the preview (create / update / skip with the reason), confirm.
 * Nothing writes until Confirm. Re-uploads update by the file's ID.
 */
export function RaidImportPanel({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<RaidImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const importer = useImportConcerns(projectId);

  const pick = (f: File | null) => {
    setFile(f); setPreview(null); setError(null);
    if (!f) return;
    importer.mutate({ file: f, commit: false }, {
      onSuccess: (d) => setPreview(d),
      onError: (e: any) => setError(e?.response?.data?.detail ?? "Couldn't read the file."),
    });
  };
  const confirm = () => {
    if (!file) return;
    importer.mutate({ file, commit: true }, {
      onSuccess: (d) => { toast.success(`Imported — ${d.created} added, ${d.updated} updated${d.skipped ? `, ${d.skipped} skipped` : ""}`); onClose(); },
      onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Import failed."),
    });
  };
  const writable = preview ? preview.counts.create + preview.counts.update : 0;

  return (
    <div className="rounded-lg border border-line bg-raised/60 p-4" data-testid="raid-import">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          <h3 className="text-sm font-semibold text-ink-2">Import from file</h3>
          <span className="text-[11px] text-ink-subtle">.xlsx or .csv · the ID column keeps re-uploads from duplicating</span>
        </div>
        <button onClick={onClose} className="rounded p-1 text-ink-subtle hover:bg-raised hover:text-ink-3" aria-label="Close import"><X className="h-3.5 w-3.5" /></button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" data-testid="raid-import-file"
          onChange={(e) => pick(e.target.files?.[0] ?? null)} />
        <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-raised">
          <Upload className="h-3.5 w-3.5" /> {file ? "Choose another file" : "Choose file"}
        </button>
        {file && <span className="text-xs text-ink-muted">{file.name}</span>}
        <button onClick={() => downloadRaidTemplate(projectId).catch(() => toast.error("Couldn't download the template"))}
          className="ml-auto inline-flex items-center gap-1 text-[11px] text-brand-navy-600 dark:text-brand-navy-300 hover:underline" data-testid="raid-template">
          <Download className="h-3 w-3" /> Download template
        </button>
      </div>

      {importer.isPending && !preview && <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-ink-muted"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the file…</p>}
      {error && <p className="mt-3 rounded-md bg-rose-50 dark:bg-rose-500/10 px-3 py-2 text-xs text-rose-700 dark:text-rose-300" data-testid="raid-import-error">{error}</p>}

      {preview && (
        <div className="mt-3">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs" data-testid="raid-import-counts">
            <Pill tone="emerald" label={`${preview.counts.create} to add`} />
            <Pill tone="sky" label={`${preview.counts.update} to update`} />
            <Pill tone={preview.counts.skip ? "amber" : "muted"} label={`${preview.counts.skip} skipped`} />
            <span className="text-ink-subtle">Nothing is saved until you confirm.</span>
          </div>
          <div className="max-h-72 overflow-auto rounded-md border border-line bg-surface">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-raised text-[10px] uppercase tracking-wider text-ink-subtle">
                <tr><th className="px-2 py-1.5">Row</th><th className="px-2 py-1.5">ID</th><th className="px-2 py-1.5">Type</th><th className="px-2 py-1.5">Title</th><th className="px-2 py-1.5">Status</th><th className="px-2 py-1.5">Action</th></tr>
              </thead>
              <tbody className="divide-y divide-line-soft">
                {preview.rows.map((r) => (
                  <tr key={r.row} data-testid="raid-import-row" data-action={r.action} className={cn(r.action === "skip" && "bg-amber-50/50 dark:bg-amber-500/5")}>
                    <td className="px-2 py-1 tabular-nums text-ink-subtle">{r.row}</td>
                    <td className="whitespace-nowrap px-2 py-1 font-mono">{r.ref ?? "—"}</td>
                    <td className="px-2 py-1 capitalize">{r.type ?? "—"}</td>
                    <td className="max-w-md truncate px-2 py-1" title={r.title ?? undefined}>{r.title ?? (r.reason ? <span className="text-amber-700 dark:text-amber-300">{r.reason}</span> : "—")}</td>
                    <td className="px-2 py-1">{r.status ?? "—"}</td>
                    <td className="px-2 py-1">
                      <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold",
                        r.action === "create" ? "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                        : r.action === "update" ? "bg-sky-50 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300"
                        : "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300")}>
                        {r.action === "create" ? "Add" : r.action === "update" ? "Update" : "Skip"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center justify-end gap-2">
            <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-xs text-ink-3 hover:bg-raised">Cancel</button>
            <button onClick={confirm} disabled={writable === 0 || importer.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700 disabled:opacity-50"
              data-testid="raid-import-confirm">
              {importer.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Import {writable} {writable === 1 ? "entry" : "entries"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Pill({ tone, label }: { tone: "emerald" | "sky" | "amber" | "muted"; label: string }) {
  const cls = {
    emerald: "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    sky: "bg-sky-50 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",
    amber: "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
    muted: "bg-raised text-ink-muted",
  }[tone];
  return <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", cls)}>{label}</span>;
}
